import React, { useEffect, useRef, useState } from "react";
import { Form, FormControl, Button, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../Constants";

const NavbarSearch = ({ onSearchComplete }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suppressSuggestions, setSuppressSuggestions] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Set initial query from URL if it exists, clear it when not on search page
  useEffect(() => {
    const urlQuery = new URLSearchParams(location.search).get("query");
    if (location.pathname === "/search" && urlQuery) {
      setQuery(urlQuery);
      setSuggestions([]);
      setSuggestLoading(false);
    } else if (location.pathname !== "/search") {
      setQuery(""); // Clear the search bar when navigating to a non-search page
      setSuggestions([]);
      setSuggestLoading(false);
    }
  }, [location]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const trimmedQuery = query.trim();
    if (suppressSuggestions) {
      setSuggestions([]);
      setSuggestLoading(false);
      return undefined;
    }

    if (trimmedQuery.length < 2) {
      setSuggestions([]);
      setSuggestLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setSuggestLoading(true);
        const response = await fetch(
          `${BASE_URL}/api/content/suggest?term=${encodeURIComponent(trimmedQuery)}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          setSuggestions([]);
          return;
        }

        const data = await response.json();
        setSuggestions(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to fetch suggestions:", error);
        setSuggestions([]);
      } finally {
        setSuggestLoading(false);
      }
    }, 220);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [query, suppressSuggestions]);

  const handleSearch = async (term) => {
    if (term.trim()) {
      setLoading(true); // Show loading spinner
      setSuggestions([]);
      setSuggestLoading(false);
      setSuppressSuggestions(true);
      navigate(`/search?query=${encodeURIComponent(term)}`);
      setLoading(false); // Hide loading spinner after navigation
      if (onSearchComplete) {
        onSearchComplete(); // Collapse the navbar if provided
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch(query);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSuggestions([]);
    setSuggestLoading(false);
    setSuppressSuggestions(true);
    setQuery(suggestion.titlePrimary || "");
    navigate(`/search?query=${encodeURIComponent(suggestion.titlePrimary || "")}`);
    if (onSearchComplete) {
      onSearchComplete();
    }
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setSuppressSuggestions(false);
  };

  return (
    <Form
      ref={wrapperRef}
      className="responsive-search-bar"
      onSubmit={(e) => {
        e.preventDefault();
        handleSearch(query);
      }}
    >
      <div className="search-shell">
        <FormControl
          type="search"
          placeholder="भजन खोजें"
          className="search-box"
          aria-label="भजन खोजें"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onKeyUp={handleKeyPress}
          autoComplete="off"
        />
        <Button
          type="submit"
          variant="custom"
          className="search-button"
          onClick={() => handleSearch(query)}
          disabled={loading}
        >
          {loading ? <Spinner as="span" animation="border" size="sm" /> : "Search"}
        </Button>
      </div>
      {(suggestLoading || suggestions.length > 0) && (
        <div className="search-suggest-panel">
          {suggestLoading ? (
            <div className="search-suggest-item search-suggest-state">Searching...</div>
          ) : (
            suggestions.map((suggestion) => (
              <button
                key={suggestion.contentId}
                type="button"
                className="search-suggest-item"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion.titlePrimary}
              </button>
            ))
          )}
        </div>
      )}
    </Form>
  );
};

export default NavbarSearch;
